import { Trash2 } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import type { ProjectListItem } from "../../shared/api-types";
import { apiError, useT } from "../i18n";
import { apiJson } from "../lib/api";
import { Button, Input, Modal } from "./ui";

export function ProjectEditModal({
  project,
  mode = "edit",
  onClose,
  onSaved,
}: {
  project: ProjectListItem | null;
  /** "delete" öffnet gleich die Bestätigungsseite — für den Löschen-Eintrag im Menü */
  mode?: "edit" | "delete";
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  // Löschen ist kein zweiter Dialog, sondern die zweite Seite desselben:
  // gestapelte Modals verwirren mehr, als sie trennen.
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const t = useT();

  useEffect(() => {
    if (project) {
      setName(project.name);
      setAddress(project.address ?? "");
      setConfirmDelete(mode === "delete");
      setBusy(false);
      setError(null);
    }
  }, [project, mode]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!project) return;
    try {
      await apiJson(`/api/projects/${project.id}`, {
        method: "PATCH",
        json: { name, address: address.trim() || null },
      });
      onSaved();
      onClose();
    } catch (err) {
      setError(apiError(t, (err as Error).message));
    }
  }

  async function remove() {
    if (!project) return;
    setBusy(true);
    try {
      await apiJson(`/api/projects/${project.id}`, { method: "DELETE" });
      onSaved();
      onClose();
    } catch (err) {
      setError(apiError(t, (err as Error).message));
      setBusy(false);
    }
  }

  const tourCount = project?.tours.length ?? 0;
  const hasPublished = Boolean(project?.tours.some((x) => x.status === "published"));

  return (
    <Modal
      open={project !== null}
      onClose={onClose}
      title={confirmDelete ? t.project.deleteTitle(project?.name ?? "") : t.project.editTitle}
    >
      {confirmDelete ? (
        <>
          <p className="mb-4 text-sm text-zinc-500 dark:text-zinc-400">
            {tourCount === 0 ? t.project.deleteTextEmpty : t.project.deleteText(tourCount)}
            {hasPublished && (
              <>
                <br />
                {t.project.deletePublished}
              </>
            )}
          </p>
          {error && <p className="mb-4 text-sm text-red-600 dark:text-red-400">{error}</p>}
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              onClick={() => (mode === "delete" ? onClose() : setConfirmDelete(false))}
              disabled={busy}
            >
              {t.common.cancel}
            </Button>
            <Button variant="danger" onClick={() => void remove()} disabled={busy}>
              {t.common.deleteForever}
            </Button>
          </div>
        </>
      ) : (
        <form onSubmit={(e) => void submit(e)} className="space-y-4">
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-zinc-700 dark:text-zinc-300">
              {t.project.nameLabel}
            </span>
            <Input
              className="w-full"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoFocus
            />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-zinc-700 dark:text-zinc-300">
              {t.project.addressLabel}{" "}
              <span className="font-normal text-zinc-400">({t.common.optional})</span>
            </span>
            <Input
              className="w-full"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder={t.project.addressPlaceholder}
            />
          </label>
          {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
          {/* Löschen steht abseits der Speichern-Gruppe — greifbar, aber nicht auf dem Weg */}
          <div className="flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={() => {
                setError(null);
                setConfirmDelete(true);
              }}
              className="inline-flex items-center gap-1.5 rounded-lg px-1.5 py-1 text-sm text-red-600 transition-colors hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/40"
            >
              <Trash2 className="size-3.5" />
              {t.project.delete}
            </button>
            <div className="flex gap-2">
              <Button type="button" variant="ghost" onClick={onClose}>
                {t.common.cancel}
              </Button>
              <Button type="submit">{t.common.save}</Button>
            </div>
          </div>
        </form>
      )}
    </Modal>
  );
}
